// The locale data for every language this widget speaks, so that setting Kendo's locale can never
// fail with "missing locale info". A host that loads its own is unaffected: registering the same
// locale twice is idempotent. These imports are deliberately in this file, which is always part of
// the bundle, rather than in one of their own that a bundler could drop as unused.
import "@progress/kendo-angular-intl/locales/en/all";
import "@progress/kendo-angular-intl/locales/it/all";
import "@progress/kendo-angular-intl/locales/de/all";

import { Injectable, inject, PLATFORM_ID, signal } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { DEFAULT_LANGUAGE, LANGUAGES } from "../constants";
import { isPlatformBrowser } from "@angular/common";
import { CldrIntlService, IntlService } from "@progress/kendo-angular-intl";

/** Where the chosen language is remembered, for the surfaces where this widget is the application. */
const LANGUAGE_STORAGE_KEY = "lang";

/**
 * The language this widget speaks, which is **the host's** whenever the host has one.
 *
 * A host shares its translation service with this library, so choosing a language here changes
 * the language of the whole application. This service therefore follows rather than decides: it
 * picks a language only when nobody else has, which is the case when this widget is the
 * application itself rather than a part of someone else's.
 */
@Injectable({ providedIn: "root" })
export class LanguageService {
    private readonly platformId = inject(PLATFORM_ID);
    private readonly translateService = inject(TranslateService);
    private readonly intlService = inject(IntlService);

    private readonly _currentLanguage = signal<string>(DEFAULT_LANGUAGE);
    readonly currentLanguage = this._currentLanguage.asReadonly();

    readonly supportedLanguages = LANGUAGES;

    constructor() {
        this.translateService.addLangs(this.supportedLanguages);
        this.translateService.onLangChange.subscribe((event) => this.follow(event.lang));

        const chosenByHost = this.translateService.getCurrentLang();
        if (chosenByHost) {
            this.follow(chosenByHost);
            return;
        }

        if (isPlatformBrowser(this.platformId)) {
            this.changeLanguage(this.preferredLanguage());
        }
    }

    /**
     * Asks for a language, which an iframe host does through the query parameter it loads this
     * widget with. Unknown languages are ignored rather than guessed at.
     */
    changeLanguage(newLang: string): void {
        const lang = newLang.toLowerCase();
        if (!this.supportedLanguages.includes(lang)) {
            return;
        }
        this.translateService.use(lang);
    }

    /**
     * A language asked for from outside the widget: by the page that frames it, or by the frame's
     * own URL.
     *
     * One it does not speak falls back to English rather than being ignored: whoever asked has
     * already moved on to that language, and leaving the widget in the previous one would be the
     * one screen on the page still speaking it.
     */
    applyRequestedLanguage(lang: string | null | undefined): void {
        const asked = lang?.trim().toLowerCase();
        this.changeLanguage(asked && this.supportedLanguages.includes(asked) ? asked : DEFAULT_LANGUAGE);
    }

    /** Takes note of the language in use and lines Kendo's formatting up with it. */
    private follow(lang: string): void {
        this._currentLanguage.set(lang);

        // Only the languages whose locale data this library carries: anything else is the host's
        // own business, and writing it here would throw for want of that data.
        if (this.supportedLanguages.includes(lang)) {
            (this.intlService as CldrIntlService).localeId = lang;
        }

        try {
            localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
        } catch {
            /* storage unavailable: the choice simply is not remembered */
        }
    }

    /** What was chosen last, what the browser asks for, or the default. In that order. */
    private preferredLanguage(): string {
        let stored: string | null = null;
        try {
            stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
        } catch {
            /* storage unavailable */
        }

        const remembered = stored?.toLowerCase();
        if (remembered && this.supportedLanguages.includes(remembered)) {
            return remembered;
        }

        const browser = this.translateService.getBrowserLang();
        return browser && this.supportedLanguages.includes(browser) ? browser : DEFAULT_LANGUAGE;
    }
}
